import { Alert, AlertTitle, Snackbar, Typography } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { monoFontFamily } from '@/theme/theme';
import type { NormalisedError } from '@/types/api';

type Severity = 'success' | 'info' | 'warning' | 'error';

interface Toast {
  id: number;
  message: string;
  severity: Severity;
  title?: string;
  /** Shown in small print so a user can quote it in a support ticket. */
  correlationId?: string;
}

interface ToastApi {
  success: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
  error: (message: string, correlationId?: string) => void;
  /** Surfaces a normalised API failure with its correlation ID already attached. */
  apiError: (error: NormalisedError, fallbackMessage?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The single snackbar layer (brief §6).
 *
 * One toast is shown at a time and queued behind the current one, rather than stacking. A
 * stack of five failures covers the content the user is trying to read, and only the first is
 * usually actionable anyway.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Toast[]>([]);

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    setQueue((current) => [...current, { ...toast, id: Date.now() + Math.random() }]);
  }, []);

  const handleClose = useCallback((_event: unknown, reason?: string) => {
    // A message the user has not read yet should not vanish because they clicked elsewhere.
    if (reason === 'clickaway') {
      return;
    }
    setQueue((current) => current.slice(1));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push({ message, severity: 'success' }),
      info: (message) => push({ message, severity: 'info' }),
      warning: (message) => push({ message, severity: 'warning' }),
      error: (message, correlationId) => push({ message, severity: 'error', correlationId }),
      apiError: (error, fallbackMessage) =>
        push({
          message: error.message || fallbackMessage || 'Something went wrong.',
          severity: 'error',
          title: error.code === 'NETWORK_ERROR' ? 'Connection problem' : undefined,
          correlationId: error.correlationId,
        }),
    }),
    [push],
  );

  const current = queue[0];

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        // `key` restarts the auto-hide timer when the next queued toast takes over; without
        // it the second message inherits whatever time was left on the first.
        key={current?.id}
        open={Boolean(current)}
        autoHideDuration={current?.severity === 'error' ? 10_000 : 4_000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleClose}
          severity={current?.severity ?? 'info'}
          variant="filled"
          sx={{ maxWidth: 480 }}
        >
          {current?.title && <AlertTitle>{current.title}</AlertTitle>}
          {current?.message}
          {current?.correlationId && (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 0.5, opacity: 0.85, fontFamily: monoFontFamily }}
            >
              ref: {current.correlationId}
            </Typography>
          )}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return context;
}
